import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import EntityActions from "crqlar-react-common/dist/components/EntityActions";
import UnsavedChangesDialog from "crqlar-react-common/dist/components/UnsavedChangesDialog";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import type {BookingEngine} from "crqlar-react-common/dist/models/bookingEngine/bookingEngine.model";
import {BookingEngineService} from "crqlar-react-common/dist/services/bookingEngine/bookingEngine.service";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import {getDialCode} from "crqlar-react-common/dist/utils/country.utils";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useUnsavedChangesWarning} from "crqlar-react-common/dist/utils/useUnsavedChangesWarning";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import {isRequiredTextMissing} from "src/utils/requiredField.util";
import BookingEngineBasicTab from "./BookingEngineBasicTab";
import BookingEngineInstructions from "./BookingEngineInstructions";
import BookingEngineStylingTab from "./BookingEngineStylingTab";
import BookingEngineTrackingTab from "./BookingEngineTrackingTab";
import Preview from "./Preview.component";

type BookingEngineDetailsTab = "basic" | "styling" | "localization" | "instructions" | "tracking";

const BookingEngineDetails = () => {
    useTrackScreenVisit("booking engine details");

    const {t} = useTranslation();
    const {id} = useParams<"id">();
    const navigate = useNavigate();
    const organization = useOrganization();
    const readOnly = useReadOnly();
    const [activeTab, setActiveTab] = useState<BookingEngineDetailsTab>("basic");

    const bookingEngineData = useEntity({
        get: async () => {
            if (id && id !== "new") {
                return BookingEngineService.getOne(id);
            }

            return {
                name: "",
                uiConfig: {
                    defaultLanguageCode: organization?.primaryLanguage,
                    defaultCountryCodePrefix: getDialCode(organization?.address?.countryCode),
                },
                formConfig: {
                    emailMandatory: true,
                    createOrAssignProfile: true,
                    timeSelection: true,
                },
                confirmationConfig: {
                    autoApprove: true,
                },
                cartConfig: {
                    allowMultipleRes: false,
                },
            } as BookingEngine;
        },
        create: BookingEngineService.create,
        update: BookingEngineService.update,
        delete: bookingEngineId => BookingEngineService.delete({id: [bookingEngineId]}),
        onDeleted: () => navigate("/bookingEngine/configurations"),
        onSaved: (bookingEngine, wasCreateAction) => {
            if (wasCreateAction) {
                navigate(`/bookingEngine/configurations/${bookingEngine.id}`, {replace: true});
            }
        },
        showSaveSuccess: true,
        isValid: bookingEngine => Boolean(bookingEngine.name?.trim()),
        cleanupBeforeSave: bookingEngine => ({
            ...bookingEngine,
            name: bookingEngine.name?.trim(),
        }),
    });

    const bookingEngine = bookingEngineData.item;
    const setBookingEngine = bookingEngineData.setItem;
    const nameMissing = isRequiredTextMissing(bookingEngineData, bookingEngine.name);
    const {showDialog, handleConfirmNavigation, handleCancelNavigation} = useUnsavedChangesWarning({
        hasUnsavedChanges: bookingEngineData.isDirty && !bookingEngineData.isMissing && !readOnly,
        isSaving: bookingEngineData.saving,
    });

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minWidth: 0,
                minHeight: 0,
            }}
        >
            <Box
                component="header"
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minHeight: 64,
                    flexShrink: 0,
                    px: 2,
                    py: 1,
                    boxSizing: "border-box",
                }}
            >
                <IconButton
                    aria-label={t("Back to booking engines")}
                    onClick={() => navigate("/bookingEngine/configurations")}
                >
                    <ArrowBackIcon/>
                </IconButton>

                <Typography variant="h5" noWrap sx={{minWidth: 0, fontWeight: 600}}>
                    {bookingEngine.name || "-"}
                </Typography>

                <Box
                    sx={{
                        ml: "auto",
                        flexShrink: 0,
                        "& > .MuiStack-root": {gap: 1},
                    }}
                >
                    <EntityActions data={bookingEngineData}/>
                </Box>
            </Box>

            <Divider/>

            {/*
              * PageShell now owns exactly 100dvh and clips its own overflow, delegating scroll
              * to each tab. Unlike the list tab (which scrolls via RightSidebarRailLayout), this
              * details form has no inner scroll container, so its tall content was being clipped.
              * minHeight:0 lets this content box shrink below its natural height and overflow:auto
              * makes it the scroll container — the header above stays pinned.
              */}
            <Box sx={{flex: 1, minHeight: 0, overflow: "auto", p: 3}}>
                <Stack
                    direction="row"
                    gap={12}
                    justifyContent="center"
                    alignItems="flex-start"
                >
                    <Stack
                        sx={{
                            width: "100%",
                            maxWidth: 800,
                            gap: 1,
                        }}
                    >
                        <Tabs
                            value={activeTab}
                            onChange={(_event, value) => setActiveTab(value)}
                            aria-label={t("Booking engine details")}
                            sx={{mb: 4}}
                        >
                            <Tab label={t("Basic info")} value="basic"/>
                            <Tab label={t("Styling")} value="styling"/>

                            {/*Hidden - product decision*/}
                            {/*<Tab label={t('Localization')} value={'localization'}/>*/}
                            <Tab label={t("Integration instructions")} value="instructions"/>
                            <Tab label={t("Tracking")} value="tracking"/>
                        </Tabs>

                        {activeTab === "basic" && (
                            <BookingEngineBasicTab
                                bookingEngine={bookingEngine}
                                setBookingEngine={setBookingEngine}
                                nameMissing={nameMissing}
                                focusNameOnLoad={bookingEngineData.isNew && !bookingEngineData.loading}
                            />
                        )}

                        {activeTab === "styling" && (
                            <BookingEngineStylingTab
                                bookingEngine={bookingEngine}
                                setBookingEngine={setBookingEngine}
                                bookingEngineData={bookingEngineData}
                            />
                        )}

                        {/*Hidden - product decision*/}
                        {/*{activeTab === 'localization' && (*/}
                        {/*    <BookingEngineLocalizationTab*/}
                        {/*        bookingEngine={bookingEngine}*/}
                        {/*        setBookingEngine={setBookingEngine}*/}
                        {/*    />*/}
                        {/*)}*/}

                        {activeTab === "instructions" && (
                            <BookingEngineInstructions bookingEngine={bookingEngine}/>
                        )}

                        {activeTab === "tracking" && (
                            <BookingEngineTrackingTab
                                bookingEngine={bookingEngine}
                                setBookingEngine={setBookingEngine}
                            />
                        )}
                    </Stack>

                    {!bookingEngineData.loading && (
                        bookingEngineData.isNew ? (
                            <Box
                                sx={{
                                    position: "sticky",
                                    top: "40px",
                                    width: 320,
                                    maxWidth: "100%",
                                    flexShrink: 0,
                                    alignSelf: "flex-start",
                                    boxSizing: "border-box",
                                    p: 2,
                                    border: "1px dashed",
                                    borderColor: "divider",
                                    borderRadius: 2,
                                    bgcolor: "grey.50",
                                }}
                            >
                                <Typography variant="body2" color="text.secondary" textAlign="center">
                                    {t("Create the booking engine to see its live preview.")}
                                </Typography>
                            </Box>
                        ) : (
                            <Preview
                                widgetName="booking-widget"
                                className="crqlar-booking-widget"
                                widgetId={bookingEngine.id}
                                widgetConfig={bookingEngineData.originalItem}
                                uiConfig={bookingEngine.uiConfig}
                            />
                        )
                    )}
                </Stack>
            </Box>

            <UnsavedChangesDialog
                open={showDialog}
                onConfirm={handleConfirmNavigation}
                onCancel={handleCancelNavigation}
            />
        </Box>
    );
};

export default BookingEngineDetails;
